import React, { useState } from "react";
import { CheckCircle2, Circle, Clock, X } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "");

const STATUS = {
  Identified: { cls: "bg-slate-100 text-slate-700", Icon: Circle },
  "In Progress": { cls: "bg-amber-100 text-amber-800", Icon: Clock },
  Completed: { cls: "bg-green-100 text-green-800", Icon: CheckCircle2 },
};

const StatusPill = ({ status, testId }) => {
  const { cls, Icon } = STATUS[status];
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${cls}`} data-testid={testId}><Icon className="w-3.5 h-3.5" />{status}</span>;
};

const Item = ({ item, idx, onPhoto, testId }) => (
  <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-2" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <p className="font-semibold text-slate-900">{item.area} — {item.element}</p>
      <StatusPill status={item.status} testId={`${testId}-status`} />
    </div>
    <p className="text-sm text-slate-700">{item.issue}{item.details ? `. ${item.details}` : ""}</p>
    {item.photos.length > 0 && (
      <div className="flex gap-2">
        {item.photos.map((src, i) => (
          <button key={i} type="button" onClick={() => onPhoto(src)} className="focus:outline-none" data-testid={`${testId}-photo-${i}`}>
            <img src={src} alt={`Item ${idx + 1} photo ${i + 1}`} className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
          </button>
        ))}
      </div>
    )}
    {item.completion_photos?.length > 0 && (
      <div className="rounded-lg bg-green-50 border border-green-200 p-2.5 space-y-1.5" data-testid={`${testId}-after`}>
        <p className="text-xs font-bold uppercase tracking-wide text-green-800">After — completed work</p>
        <div className="flex gap-2">
          {item.completion_photos.map((p, i) => (
            <button key={i} type="button" onClick={() => onPhoto(`${process.env.REACT_APP_BACKEND_URL}${p.full}`)} data-testid={`${testId}-after-photo-${i}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${p.thumb}`} alt={`Completed work ${i + 1}`} className="w-20 h-20 rounded-lg object-cover border border-green-300" />
            </button>
          ))}
        </div>
      </div>
    )}
    <p className="text-xs text-slate-500">
      Identified {fmtDate(item.identified_at)}
      {item.completed_at && <span className="text-green-700 font-medium"> · Completed {fmtDate(item.completed_at)}</span>}
      {item.added_after_walkthrough && <span> · Added after walkthrough</span>}
    </p>
  </div>
);

const Counts = ({ c, total }) => (
  <div className="space-y-2">
    <div className="h-2 rounded-full bg-slate-200 overflow-hidden flex">
      <div className="bg-green-500" style={{ width: `${(c.Completed / total) * 100}%` }} />
      <div className="bg-amber-400" style={{ width: `${(c["In Progress"] / total) * 100}%` }} />
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
      <span><b className="text-slate-900">{total}</b> item{total === 1 ? "" : "s"}</span>
      <span><b className="text-slate-700">{c.Identified}</b> identified</span>
      <span><b className="text-amber-700">{c["In Progress"]}</b> in progress</span>
      <span><b className="text-green-700">{c.Completed}</b> completed</span>
    </div>
  </div>
);

export const HandoverWalkthroughSection = ({ w, dwelling }) => {
  const [photo, setPhoto] = useState(null);
  const base = `handover-wt-${dwelling}-${w.sequence}`;
  const total = w.items.length;
  return (
    <div className="space-y-3" data-testid={base}>
      <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold text-slate-900">Walkthrough #{w.sequence} · {fmtDate(w.started_at)}</p>
          {w.status === "in_progress"
            ? <span className="text-xs font-bold rounded-full bg-sky-100 text-sky-800 px-2.5 py-1" data-testid={`${base}-live`}>Being recorded now</span>
            : <span className="text-xs text-slate-500">{w.acknowledged ? `Acknowledged ${fmtDate(w.acknowledged_at)}` : `Finished ${fmtDate(w.finished_at)}`}</span>}
        </div>
        {total > 0 && <Counts c={w.counts} total={total} />}
      </div>
      {w.items.map((it, i) => <Item key={i} item={it} idx={i} onPhoto={setPhoto} testId={`${base}-item-${i}`} />)}
      {photo && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPhoto(null)} data-testid="handover-photo-viewer">
          <button type="button" className="absolute top-4 right-4 text-white" aria-label="Close photo"><X className="w-7 h-7" /></button>
          <img src={photo} alt="" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};
